import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import apiClient from "utils/apiClient";
import tokenRefreshService from "utils/tokenRefreshService";
import AuthLoading from "components/common/AuthLoading";
import paths from "routes/paths";
import { ensureClientId } from "utils/clientBootstrap";
import { O_API_KEY, O_CLIENT_APP } from "constants";
import { normalizeIsoExpiry } from "utils/normalizeIsoExpiry";
import { useMultiTabAuth } from "hooks/auth/useMultiTabAuth";
import {
  clearPostLoginRedirect,
  getCurrentAppPath,
  savePostLoginRedirect,
} from "utils/postLoginRedirect";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialCheck, setIsInitialCheck] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [accessTokenExpiresAt, setAccessTokenExpiresAt] = useState(null);
  const [refreshTokenExpiresAt, setRefreshTokenExpiresAt] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [userPrivileges, setUserPrivileges] = useState([]);
  const logoutRef = useRef(() => {});

  const { isLeader, requestSession, broadcastLogout, broadcastTokenUpdate } =
    useMultiTabAuth({
      onSessionReceived: () => {
        checkAuthStatus();
      },
      onLogoutReceived: () => {
        logoutRef.current(true);
      },
      onTokensUpdated: () => {
        // Token refresh should stay silent and must not disrupt in-progress UI.
        // We persist refreshed tokens in storage and let request interceptors read from there.
      },
    });

  const logout = useCallback(
    async (isRemote = false, redirectPath = "/auth/login") => {
      // Cleanup token refresh service
      tokenRefreshService.cleanup();

      if (redirectPath === paths.simpleSessionExpired) {
        savePostLoginRedirect(getCurrentAppPath());
      } else {
        clearPostLoginRedirect();
      }

      // Clear all auth state
      setUser(null);
      setIsAuthenticated(false);
      setAuthToken(null);
      setRefreshToken(null);
      setAccessTokenExpiresAt(null);
      setRefreshTokenExpiresAt(null);
      setUserPrivileges([]);
      // Clear storage
      sessionStorage.clear();

      // only broadcast if this tab initiated logout
      if (!isRemote) {
        broadcastLogout();
      }
      window.location.href = redirectPath;
      return true;
    },
    [broadcastLogout],
  );

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  const bootstrapClient = async () => {
    try {
      const id = await ensureClientId();
      setClientId(id);
      return true;
    } catch (error) {
      console.error("Client bootstrap failed:", error);
      return false;
    }
  };

  const checkAuthStatus = useCallback(async () => {
    try {
      const storedToken = sessionStorage.getItem("authToken");
      const storedRefreshToken = sessionStorage.getItem("refreshToken");
      const storedAccessTokenExpiresAt = sessionStorage.getItem(
        "accessTokenExpiresAt",
      );
      const storedRefreshTokenExpiresAt = sessionStorage.getItem(
        "refreshTokenExpiresAt",
      );
      const storedPrivileges = JSON.parse(
        sessionStorage.getItem("userPrivileges") || "[]",
      );

      if (!storedToken) {
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
        setIsInitialCheck(false);
        return null;
      }

      // Update state with stored values
      setAuthToken(storedToken);
      setRefreshToken(storedRefreshToken);
      setAccessTokenExpiresAt(normalizeIsoExpiry(storedAccessTokenExpiresAt));
      setRefreshTokenExpiresAt(storedRefreshTokenExpiresAt);
      setUserPrivileges(storedPrivileges);

      // Verify token and get basic identity
      const identityRes = await apiClient.get(
        "/identity-service/api/v1/users/me",
        {
          // headers: { Authorization: `Bearer ${storedToken}` },
        },
      );

      if (identityRes.data && identityRes.data.id) {
        // Fetch FULL profile after identity confirmed
        const profileRes = await apiClient.get(
          "/profile-service/api/v1/user-profiles/get?refresh=true",
          {
            headers: { Authorization: `Bearer ${storedToken}` },
          },
        );

        let userData = null;

        if (profileRes.data && profileRes.data.id) {
          userData = profileRes.data;
          setUser(userData);
          setUserPrivileges(storedPrivileges);
          setIsAuthenticated(true);
        } else {
          // Profile fetch failed, fallback to identity
          userData = identityRes.data;
          setUser(userData);
          setIsAuthenticated(true);
        }

        return userData;
      } else {
        // Token invalid or identity not found
        sessionStorage.removeItem("authToken");
        setUser(null);
        setIsAuthenticated(false);
        return null;
      }
    } catch (error) {
      console.error("Auth check failed:", error);

      // Handle 401 errors
      if (error.response?.status === 401) {
        sessionStorage.removeItem("authToken");
        sessionStorage.removeItem("refreshToken");
        sessionStorage.removeItem("accessTokenExpiresAt");
        sessionStorage.removeItem("refreshTokenExpiresAt");
        setAuthToken(null);
      }

      setUser(null);
      setIsAuthenticated(false);
      return null;
    } finally {
      setLoading(false);
      setIsInitialCheck(false);
    }
  }, []);

  // Initialize authentication on mount
  useEffect(() => {
    const initialize = async () => {
      // Step 1: Bootstrap client
      const clientReady = await bootstrapClient();
      if (!clientReady) {
        setLoading(false);
        setIsInitialCheck(false);
        return;
      }

      // Step 2: Check auth status
      await requestSession();
      await checkAuthStatus();
    };

    initialize();
  }, [checkAuthStatus]);

  // Initialize token refresh service when authenticated
  useEffect(() => {
    if (isAuthenticated && refreshToken && accessTokenExpiresAt && isLeader) {
      // Callback for when tokens are refreshed
      const handleTokenRefreshed = (tokenData) => {
        // Update session storage
        if (tokenData.accessToken) {
          sessionStorage.setItem("authToken", tokenData.accessToken);
        }
        if (tokenData.refreshToken) {
          sessionStorage.setItem("refreshToken", tokenData.refreshToken);
        }
        if (tokenData.accessTokenExpiresAt) {
          const normalizedExpiry = normalizeIsoExpiry(
            tokenData.accessTokenExpiresAt,
          );
          sessionStorage.setItem(
            "accessTokenExpiresAt",
            normalizedExpiry || "",
          );
        }
        if (tokenData.refreshTokenExpiresAt) {
          sessionStorage.setItem(
            "refreshTokenExpiresAt",
            tokenData.refreshTokenExpiresAt,
          );
        }

        broadcastTokenUpdate({
          authToken: tokenData.accessToken,
          refreshToken: tokenData.refreshToken,
          accessTokenExpiresAt: tokenData.accessTokenExpiresAt,
          refreshTokenExpiresAt: tokenData.refreshTokenExpiresAt,
          userPrivileges: sessionStorage.getItem("userPrivileges"),
        });
      };

      // Callback for when session expires
      const handleSessionExpired = () => {
        logout(false, paths.simpleSessionExpired);
      };

      // Initialize the service
      tokenRefreshService.initialize(
        handleSessionExpired,
        handleTokenRefreshed,
      );
    } else {
      // Cleanup when not authenticated
      tokenRefreshService.cleanup();
    }

    return () => {
      tokenRefreshService.cleanup();
    };
  }, [
    isAuthenticated,
    refreshToken,
    accessTokenExpiresAt,
    isLeader,
    logout,
    broadcastTokenUpdate,
  ]);

  const login = async (email, password) => {
    try {
      const { data } = await apiClient.post(
        "/identity-service/api/v1/auth/login",
        {
          email,
          password,
        },
        {
          headers: {
            "O-API-KEY": O_API_KEY,
            "O-CLIENT-APP": O_CLIENT_APP,
          },
        },
      );

      if (!data.accessToken) {
        throw new Error("No access token returned");
      }

      const {
        accessToken,
        refreshToken,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
        clientId,
        privileges,
      } = data;

      // Store all token data in sessionStorage
      sessionStorage.setItem("authToken", accessToken);
      sessionStorage.setItem("refreshToken", refreshToken || "");
      sessionStorage.setItem(
        "accessTokenExpiresAt",
        accessTokenExpiresAt || "",
      );
      sessionStorage.setItem(
        "refreshTokenExpiresAt",
        refreshTokenExpiresAt || "",
      );
      sessionStorage.setItem(
        "userPrivileges",
        JSON.stringify(privileges || []),
      );

      // Update state
      setAuthToken(accessToken);
      setRefreshToken(refreshToken || null);
      setAccessTokenExpiresAt(normalizeIsoExpiry(accessTokenExpiresAt) || null);
      setRefreshTokenExpiresAt(refreshTokenExpiresAt || null);
      setUserPrivileges(privileges || []);

      // Check auth status to fetch user profile
      const userData = await checkAuthStatus();

      return {
        success: true,
        data: userData || { email },
        user: userData,
      };
    } catch (error) {
      console.error("Login failed:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  };

  const isLoggedIn = () => !!(isAuthenticated && user);

  const refreshAuth = async () => {
    try {
      await checkAuthStatus();
    } catch (error) {
      console.error("Failed to refresh authentication:", error);
    }
};

  const value = {
    user,
    isAuthenticated,
    loading,
    isInitialCheck,
    login,
    logout,
    checkAuthStatus,
    isLoggedIn,
    refreshAuth,
    privileges: userPrivileges,
    clientId,
    authToken,
    refreshToken,
    accessTokenExpiresAt,
    refreshTokenExpiresAt,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <AuthLoading /> : children}
    </AuthContext.Provider>
  );
};

// Export only ONE default export
export default AuthProvider;
